/**
 * AI Training Page
 * 
 * HITL (Human-in-the-Loop) voice training interface for AI-AMSO.
 * Allows experts to train the AI by providing voice commands while
 * observing an airport simulation.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import UnifiedAirportMap from '../components/map-engine/UnifiedAirportMap';

import { useVoiceCapture } from '../hooks/useVoiceCapture';
import { useLanguage } from '../context/LanguageContext';
import { getTrainingSession, startTrainingSession } from '../services/aiTrainingService';
import { API_URL } from '../services/api';
import CurriculumSelector from '../components/fts/CurriculumSelector';
import {
    AITrainingSessionDetail,
    ParsedCommand,
    TranscriptionLog,
    ATCAction,
} from '../types/aiTraining';
import {
    MicrophoneIcon,
    StopIcon,
    ChevronLeftIcon,
    SignalIcon,
    SignalSlashIcon,
    CheckCircleIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    ClockIcon,
    DocumentTextIcon,
    PaperAirplaneIcon,
    MapPinIcon,
    LightBulbIcon,
} from '@heroicons/react/24/solid';

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

const ActionBadge: React.FC<{ action: ATCAction }> = ({ action }) => {
    const colorMap: Record<ATCAction, string> = {
        [ATCAction.HOLD]: 'bg-red-500',
        [ATCAction.STOP]: 'bg-red-600',
        [ATCAction.TAXI]: 'bg-blue-500',
        [ATCAction.PUSHBACK]: 'bg-purple-500',
        [ATCAction.EXPEDITE]: 'bg-yellow-500',
        [ATCAction.CLEARED]: 'bg-green-500',
        [ATCAction.GIVE_WAY]: 'bg-orange-500',
        [ATCAction.CROSS]: 'bg-cyan-500',
        [ATCAction.CONTINUE]: 'bg-emerald-500',
        [ATCAction.UNKNOWN]: 'bg-gray-500',
    };

    return (
        <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${colorMap[action]}`}>
            {action}
        </span>
    );
};

const ConfidenceMeter: React.FC<{ confidence: number }> = ({ confidence }) => {
    const percent = Math.round(confidence * 100);
    const color = confidence >= 0.8 ? 'bg-green-500' : confidence >= 0.5 ? 'bg-yellow-500' : 'bg-red-500';

    return (
        <div className="flex items-center gap-2">
            <div className="h-1.5 w-16 bg-[var(--surface-inset)] rounded-full overflow-hidden">
                <div
                    className={`h-full ${color} transition-all`}
                    style={{ width: `${percent}%` }}
                />
            </div>
            <span className="text-xs text-[var(--text-secondary)]">{percent}%</span>
        </div>
    );
};

const TranscriptionCard: React.FC<{
    log: TranscriptionLog;
    onValidate?: (id: string, approved: boolean) => void;
}> = ({ log, onValidate }) => {
    const { language } = useLanguage();
    const tx = TEXTS[(language as 'es' | 'en') || 'en'] || TEXTS.en;
    return (
        <div className="bg-[#0a1f35] rounded-lg p-3 border border-[#1e3a5f] hover:border-[#F26430]/50 transition-colors">
            {/* Transcription text */}
            <div className="flex items-start justify-between mb-2">
                <p className="text-white text-sm font-medium flex-1">
                    "{log.transcription}"
                </p>
                <span className="text-xs text-[var(--text-tertiary)] ml-2">
                    {log.timestamp.toLocaleTimeString()}
                </span>
            </div>

            {/* Parsed command */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <ActionBadge action={log.command.action} />
                {log.command.target_flight && (
                    <span className="text-xs text-cyan-400 font-mono flex items-center gap-1">
                        <PaperAirplaneIcon className="h-3 w-3" /> {log.command.target_flight}
                    </span>
                )}
                {log.command.location_node && (
                    <span className="text-xs text-yellow-400 font-mono flex items-center gap-1">
                        <MapPinIcon className="h-3 w-3" /> {log.command.location_node}
                    </span>
                )}
            </div>

            {/* Confidence & Validation */}
            <div className="flex items-center justify-between">
                <ConfidenceMeter confidence={log.command.confidence} />

                {onValidate && !log.isValidated && (
                    <div className="flex gap-1">
                        <button
                            onClick={() => onValidate(log.id, true)}
                            className="p-1 rounded text-green-500 hover:bg-green-500/20"
                            title={tx.approve}
                        >
                            <CheckCircleIcon className="h-5 w-5" />
                        </button>
                        <button
                            onClick={() => onValidate(log.id, false)}
                            className="p-1 rounded text-red-500 hover:bg-red-500/20"
                            title={tx.reject}
                        >
                            <XCircleIcon className="h-5 w-5" />
                        </button>
                    </div>
                )}

                {log.isValidated && (
                    <span className="text-xs text-green-400">{tx.validated}</span>
                )}
            </div>
        </div>
    );
};

// =============================================================================
// MAIN PAGE COMPONENT
// =============================================================================

const TEXTS = {
    es: {
        loadingSession: 'Cargando sesión de entrenamiento...',
        error: 'Error',
        goBack: 'Volver',
        missingToken: 'Token de Mapbox Ausente',
        configureToken: 'Configurar VITE_MAPBOX_TOKEN en .env',
        commands: 'comandos',
        voiceControl: 'Control de Voz',
        connected: 'Conectado',
        reconnect: 'Reconectar',
        startSession: 'Iniciar Sesión de Entrenamiento',
        releaseToSend: 'Suelta para enviar',
        processing: 'Procesando...',
        holdToSpeak: 'Mantén para hablar',
        connecting: 'Conectando...',
        typePlaceholder: 'Escribe un comando (prueba)...',
        send: 'Enviar',
        exHint: 'Ej: "Hold position at Bravo-Two"',
        recentTranscriptions: 'Transcripciones Recientes',
        clear: 'Limpiar',
        noTranscriptions: 'Sin transcripciones aún',
        holdButton: 'Mantén el botón y habla',
        startToBegin: 'Inicia la sesión para comenzar',
        thisSession: 'Esta sesión',
        highConfidence: 'Alta confianza',
        total: 'Total',
        validated: '✓ Validado',
        approve: 'Aprobar',
        reject: 'Rechazar',
    },
    en: {
        loadingSession: 'Loading training session...',
        error: 'Error',
        goBack: 'Go Back',
        missingToken: 'Missing Mapbox Token',
        configureToken: 'Configure VITE_MAPBOX_TOKEN in .env',
        commands: 'commands',
        voiceControl: 'Voice Control',
        connected: 'Connected',
        reconnect: 'Reconnect',
        startSession: 'Start Training Session',
        releaseToSend: 'Release to send',
        processing: 'Processing...',
        holdToSpeak: 'Hold to speak',
        connecting: 'Connecting...',
        typePlaceholder: 'Type a command (testing)...',
        send: 'Send',
        exHint: 'Ex: "Hold position at Bravo-Two"',
        recentTranscriptions: 'Recent Transcriptions',
        clear: 'Clear',
        noTranscriptions: 'No transcriptions yet',
        holdButton: 'Hold the button and speak',
        startToBegin: 'Start session to begin',
        thisSession: 'This session',
        highConfidence: 'High confidence',
        total: 'Total',
        validated: '✓ Validated',
        approve: 'Approve',
        reject: 'Reject',
    },
};

const AITrainingPage: React.FC = () => {
    const { language } = useLanguage();
    const tx = TEXTS[(language as 'es' | 'en') || 'en'] || TEXTS.en;
    const { sessionId } = useParams<{ sessionId: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // State
    const [session, setSession] = useState<AITrainingSessionDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [textInput, setTextInput] = useState('');

    // Airport data (from project or defaults)
    const [airportCenter, setAirportCenter] = useState<[number, number]>([6.2044, 49.6233]);
    const [icaoCode, setIcaoCode] = useState('MPTO');

    // Voice capture hook
    const {
        isConnected,
        isRecording,
        isProcessing,
        connectionStatus,
        transcriptions,
        lastError,
        connect,
        disconnect,
        startRecording,
        stopRecording,
        sendTextCommand,
        clearTranscriptions,
    } = useVoiceCapture({
        sessionId: sessionId || '',
        onTranscription: (text, command) => {
            console.log('New transcription:', text, command);
        },
        onError: (err) => {
            console.error('Voice capture error:', err);
        },
    });

    // Load session data
    useEffect(() => {
        const loadSession = async () => {
            if (!sessionId) {
                setError('No session ID provided');
                setIsLoading(false);
                return;
            }

            try {
                const data = await getTrainingSession(sessionId);
                setSession(data);

                // Get project info for map center
                const projectId = searchParams.get('projectId') || data.project_id;
                if (projectId) {
                    const projResponse = await fetch(`${API_URL}/projects/${projectId}`);
                    if (projResponse.ok) {
                        const projData = await projResponse.json();
                        if (projData.geojson_data?.features?.[0]?.geometry?.coordinates) {
                            const coords = projData.geojson_data.features[0].geometry.coordinates;
                            setAirportCenter([coords[0], coords[1]]);
                        }
                        if (projData.icao_code) setIcaoCode(projData.icao_code);
                    }
                }

                setIsLoading(false);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load session');
                setIsLoading(false);
            }
        };

        loadSession();
    }, [sessionId, searchParams]);

    // Handle session start
    const handleStartSession = async () => {
        if (!sessionId || !session) return;

        try {
            const updated = await startTrainingSession(sessionId);
            setSession(prev => prev ? { ...prev, ...updated } : null);
            connect();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to start session');
        }
    };

    // Handle text command submission (for testing)
    const handleSendText = () => {
        if (!textInput.trim()) return;
        sendTextCommand(textInput.trim());
        setTextInput('');
    };

    // Push-to-talk handlers
    const handlePushToTalkStart = () => {
        if (!isConnected) {
            connect();
            return;
        }
        startRecording();
    };

    const handlePushToTalkEnd = () => {
        stopRecording();
    };

    // Validation handler
    const handleValidate = (id: string, approved: boolean) => {
        // TODO: Call API to validate
        console.log('Validate:', id, approved);
    };

    // Render loading state
    if (isLoading) {
        return (
            <div className="flex h-[calc(100vh-64px)] items-center justify-center bg-white">
                <div className="text-center">
                    <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-[#F26430] border-t-transparent mx-auto" />
                    <p className="text-[var(--text-secondary)]">{tx.loadingSession}</p>
                </div>
            </div>
        );
    }

    // Render error state
    if (error) {
        return (
            <div className="flex h-[calc(100vh-64px)] items-center justify-center bg-white">
                <div className="text-center max-w-md">
                    <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-white mb-2">{tx.error}</h2>
                    <p className="text-[var(--text-secondary)] mb-4">{error}</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 bg-[#F26430] text-white rounded-lg hover:bg-[#d14f1f]"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex h-[calc(100vh-64px)] bg-white text-gray-200">
                {/* ============================================================ */}
                {/* LEFT: Map Visualization (70%) */}
                {/* ============================================================ */}
                <main className="relative flex-1">
                    <UnifiedAirportMap
                        mode="fts-simulation"
                        icaoCode={icaoCode}
                        initialCenter={airportCenter}
                        initialZoom={14}
                        showLayerPanel={false}
                        className="h-full w-full"
                    />



                    {/* Session Info Overlay */}
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg p-4 border border-[#1e3a5f]">
                        <h2 className="text-lg font-bold text-white mb-1">
                            {session?.name || 'AI-AMSO Session'}
                        </h2>
                        <div className="flex items-center gap-3 text-sm">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${session?.status === 'ACTIVE' ? 'bg-green-500/20 text-green-400' :
                                session?.status === 'DRAFT' ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-gray-500/20 text-[var(--text-secondary)]'
                                }`}>
                                {session?.status}
                            </span>
                            <span className="text-[var(--text-secondary)]">
                                <DocumentTextIcon className="h-4 w-4 inline mr-1" />
                                {session?.total_commands || 0} {tx.commands}
                            </span>
                        </div>
                    </div>
                </main>

                {/* ============================================================ */}
                {/* RIGHT: Control Panel (30%) */}
                {/* ============================================================ */}
                <aside className="w-96 flex flex-col border-l border-[#1e3a5f] bg-white">
                    {/* Header */}
                    <div className="p-4 border-b border-[#1e3a5f]">
                        <button
                            onClick={() => navigate(-1)}
                            className="mb-2 inline-flex items-center text-sm text-[var(--text-secondary)] hover:text-[#F26430]"
                        >
                            <ChevronLeftIcon className="mr-1 h-4 w-4" />
                            {tx.goBack}
                        </button>
                        <h2 className="text-lg font-semibold text-white">{tx.voiceControl}</h2>

                        {/* Connection Status */}
                        <div className="mt-2 flex items-center gap-2">
                            {isConnected ? (
                                <>
                                    <SignalIcon className="h-4 w-4 text-green-500" />
                                    <span className="text-xs text-green-400">{tx.connected}</span>
                                </>
                            ) : (
                                <>
                                    <SignalSlashIcon className="h-4 w-4 text-[var(--text-tertiary)]" />
                                    <span className="text-xs text-[var(--text-tertiary)]">{connectionStatus}</span>
                                </>
                            )}

                            {!isConnected && session?.status === 'ACTIVE' && (
                                <button
                                    onClick={connect}
                                    className="ml-auto text-xs text-[#F26430] hover:underline"
                                >
                                    {tx.reconnect}
                                </button>
                            )}
                        </div>

                        {lastError && (
                            <div className="mt-2 text-xs text-red-400 bg-red-500/10 rounded px-2 py-1">
                                {lastError}
                            </div>
                        )}
                    </div>

                    {/* Curriculum Selector — prerequisite step before training */}
                    {session && (
                        <div className="p-4 border-b border-[#1e3a5f]">
                            <CurriculumSelector
                                projectId={session.project_id}
                                sessionId={session.id}
                                initialSelectedDates={
                                    (session.training_config?.training_days as string[] | undefined) ?? []
                                }
                                onSaved={(dates) => {
                                    setSession((prev) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  training_config: {
                                                      ...(prev.training_config ?? {}),
                                                      training_days: dates,
                                                  },
                                              }
                                            : null,
                                    );
                                }}
                            />
                        </div>
                    )}

                    {/* Push-to-Talk Button */}
                    <div className="p-6 border-b border-[#1e3a5f]">
                        {session?.status === 'DRAFT' ? (
                            <button
                                onClick={handleStartSession}
                                className="w-full py-4 rounded-xl bg-[#F26430] text-white font-bold hover:bg-[#d14f1f] transition-colors"
                            >
                                {tx.startSession}
                            </button>
                        ) : (
                            <button
                                onMouseDown={handlePushToTalkStart}
                                onMouseUp={handlePushToTalkEnd}
                                onMouseLeave={handlePushToTalkEnd}
                                onTouchStart={handlePushToTalkStart}
                                onTouchEnd={handlePushToTalkEnd}
                                disabled={!isConnected || isProcessing}
                                className={`w-full aspect-square max-h-48 rounded-full flex flex-col items-center justify-center gap-3 transition-all ${isRecording
                                    ? 'bg-red-500 scale-110 shadow-[0_0_40px_rgba(239,68,68,0.5)]'
                                    : isProcessing
                                        ? 'bg-yellow-500/50 animate-pulse'
                                        : isConnected
                                            ? 'bg-[#1e3a5f] hover:bg-[#2a4a6f] hover:scale-105'
                                            : 'bg-[var(--surface-inset)] cursor-not-allowed opacity-50'
                                    }`}
                            >
                                {isRecording ? (
                                    <>
                                        <StopIcon className="h-16 w-16 text-white" />
                                        <span className="text-white font-medium">{tx.releaseToSend}</span>
                                    </>
                                ) : isProcessing ? (
                                    <>
                                        <div className="h-12 w-12 border-4 border-white border-t-transparent rounded-full animate-spin" />
                                        <span className="text-white text-sm">{tx.processing}</span>
                                    </>
                                ) : (
                                    <>
                                        <MicrophoneIcon className="h-16 w-16 text-[#F26430]" />
                                        <span className="text-gray-300 font-medium">
                                            {isConnected ? tx.holdToSpeak : tx.connecting}
                                        </span>
                                    </>
                                )}
                            </button>
                        )}

                        {/* Text input for testing */}
                        <div className="mt-4">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={textInput}
                                    onChange={(e) => setTextInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSendText()}
                                    placeholder={tx.typePlaceholder}
                                    className="flex-1 px-3 py-2 rounded-lg bg-[#0a1f35] border border-[#1e3a5f] text-white text-sm placeholder-[var(--text-tertiary)] focus:border-[#F26430] focus:outline-none"
                                />
                                <button
                                    onClick={handleSendText}
                                    disabled={!isConnected || !textInput.trim()}
                                    className="px-4 py-2 rounded-lg bg-[#F26430] text-white text-sm font-medium hover:bg-[#d14f1f] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {tx.send}
                                </button>
                            </div>
                            <p className="mt-1 text-xs text-[var(--text-tertiary)] flex items-center gap-1">
                                <LightBulbIcon className="h-3 w-3" /> {tx.exHint}
                            </p>
                        </div>
                    </div>

                    {/* Transcriptions List */}
                    <div className="flex-1 overflow-y-auto p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-medium text-[var(--text-secondary)] flex items-center gap-2">
                                <ClockIcon className="h-4 w-4" />
                                {tx.recentTranscriptions}
                            </h3>
                            {transcriptions.length > 0 && (
                                <button
                                    onClick={clearTranscriptions}
                                    className="text-xs text-[var(--text-tertiary)] hover:text-gray-300"
                                >
                                    {tx.clear}
                                </button>
                            )}
                        </div>

                        {transcriptions.length === 0 ? (
                            <div className="text-center py-12 text-[var(--text-tertiary)]">
                                <MicrophoneIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                <p className="text-sm">{tx.noTranscriptions}</p>
                                <p className="text-xs mt-1">
                                    {isConnected
                                        ? tx.holdButton
                                        : tx.startToBegin}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {transcriptions.map((log) => (
                                    <TranscriptionCard
                                        key={log.id}
                                        log={log}
                                        onValidate={handleValidate}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Stats Footer */}
                    <div className="p-4 border-t border-[#1e3a5f] bg-[#0a1f35]/50">
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <div>
                                <div className="text-xl font-bold text-[#F26430]">
                                    {transcriptions.length}
                                </div>
                                <div className="text-xs text-[var(--text-tertiary)]">{tx.thisSession}</div>
                            </div>
                            <div>
                                <div className="text-xl font-bold text-green-400">
                                    {transcriptions.filter(t => t.command.confidence >= 0.8).length}
                                </div>
                                <div className="text-xs text-[var(--text-tertiary)]">{tx.highConfidence}</div>
                            </div>
                            <div>
                                <div className="text-xl font-bold text-white">
                                    {session?.total_commands || 0}
                                </div>
                                <div className="text-xs text-[var(--text-tertiary)]">{tx.total}</div>
                            </div>
                        </div>
                    </div>
                </aside>
            </div>
        </>
    );
};

export default AITrainingPage;
